import { StoreonModule } from "storeon";
import { Person } from "./models/Person";
import { Gender } from "./enums/Gender";

export interface IDependentStore {
  dependentStore: IDependentStoreShape
}

export interface IDependentStoreShape {
    spouse: Person | null;
    dependents: Person[];
}

export interface IDependentEvents {
  addSpouse: undefined;
  removeSpouse: undefined;
  setSpouseFirstName: string;
  setSpouseMiddleName: string;
  setSpouseLastName: string;
  setSpouseSuffix: string;
  setSpouseGender: Gender
  setSpouseBirthDate: Date;
  setSpouseSSN: string;

  addDependent: undefined;
  removeDependent: number;
  clearDependents: undefined;
  updateDependentFirstName: {elIndex: number; element: string};
  updateDependentMiddleName: {elIndex: number; element: string};
  updateDependentLastName: {elIndex: number; element: string};
  updateDependentSuffix: {elIndex: number; element: string};
  updateDependentGender: {elIndex: number; element: Gender};
  updateDependentBirthDate: {elIndex: number; element: Date};
  updateDependentSSN: {elIndex: number; element: string};
}

const dependentStore: StoreonModule<IDependentStore, IDependentEvents> = (store) => {
  store.on('@init', initializeDependentStore)
  store.on('addSpouse', (state) => ({
    ...state,
    dependentStore: {
      ...state.dependentStore,
      spouse: new Person(),
    },
  }))
  store.on('removeSpouse', (state) => ({
    ...state,
    dependentStore: {
      ...state.dependentStore,
      spouse: null,
    },
  }))
  store.on('setSpouseFirstName', (state, event) => ({
    ...state,
    dependentStore: {
      ...state.dependentStore,
      spouse: {
        firstName: event,
        middleName: state.dependentStore.spouse?.middleName ?? '',
        lastName: state.dependentStore.spouse?.lastName ?? '',
        suffix: state.dependentStore.spouse?.suffix ?? '',
        birthDate: state.dependentStore.spouse?.birthDate ?? new Date(''),
        gender: state.dependentStore.spouse?.gender ?? undefined,
        ssn: state.dependentStore.spouse?.ssn ?? '',
      }
    },
  }))
  store.on('setSpouseMiddleName', (state, event) => ({
    ...state,
    dependentStore: {
      ...state.dependentStore,
      spouse: {
        firstName: state.dependentStore.spouse?.firstName ?? '',
        middleName: event,
        lastName: state.dependentStore.spouse?.lastName ?? '',
        suffix: state.dependentStore.spouse?.suffix ?? '',
        birthDate: state.dependentStore.spouse?.birthDate ?? new Date(''),
        gender: state.dependentStore.spouse?.gender ?? undefined,
        ssn: state.dependentStore.spouse?.ssn ?? '',
      }
    },
  }))
  store.on('setSpouseLastName', (state, event) => ({
    ...state,
    dependentStore: {
      ...state.dependentStore,
      spouse: {
        firstName: state.dependentStore.spouse?.firstName ?? '',
        middleName: state.dependentStore.spouse?.middleName ?? '',
        lastName: event,
        suffix: state.dependentStore.spouse?.suffix ?? '',
        birthDate: state.dependentStore.spouse?.birthDate ?? new Date(''),
        gender: state.dependentStore.spouse?.gender ?? undefined,
        ssn: state.dependentStore.spouse?.ssn ?? '',
      }
    },
  }))
  store.on('setSpouseSuffix', (state, event) => ({
    ...state,
    dependentStore: {
      ...state.dependentStore,
      spouse: {
        firstName: state.dependentStore.spouse?.firstName ?? '',
        middleName: state.dependentStore.spouse?.middleName ?? '',
        lastName: state.dependentStore.spouse?.lastName ?? '',
        suffix: event,
        birthDate: state.dependentStore.spouse?.birthDate ?? new Date(''),
        gender: state.dependentStore.spouse?.gender ?? undefined,
        ssn: state.dependentStore.spouse?.ssn ?? '',
      }
    },
  }))
  store.on('setSpouseGender', (state, event) => ({
    ...state,
    dependentStore: {
      ...state.dependentStore,
      spouse: {
        firstName: state.dependentStore.spouse?.firstName ?? '',
        middleName: state.dependentStore.spouse?.middleName ?? '',
        lastName: state.dependentStore.spouse?.lastName ?? '',
        suffix: state.dependentStore.spouse?.suffix ?? '',
        birthDate: state.dependentStore.spouse?.birthDate ?? new Date(''),
        gender: event,
        ssn: state.dependentStore.spouse?.ssn ?? '',
      }
    },
  }))
  store.on('setSpouseBirthDate', (state, event) => ({
    ...state,
    dependentStore: {
      ...state.dependentStore,
      spouse: {
        firstName: state.dependentStore.spouse?.firstName ?? '',
        middleName: state.dependentStore.spouse?.middleName ?? '',
        lastName: state.dependentStore.spouse?.lastName ?? '',
        suffix: state.dependentStore.spouse?.suffix ?? '',
        birthDate: event,
        gender: state.dependentStore.spouse?.gender ?? undefined,
        ssn: state.dependentStore.spouse?.ssn ?? '',
      }
    },
  }))
  store.on('setSpouseSSN', (state, event) => ({
    ...state,
    dependentStore: {
      ...state.dependentStore,
      spouse: {
        firstName: state.dependentStore.spouse?.firstName ?? '',
        middleName: state.dependentStore.spouse?.middleName ?? '',
        lastName: state.dependentStore.spouse?.lastName ?? '',
        suffix: state.dependentStore.spouse?.suffix ?? '',
        birthDate: state.dependentStore.spouse?.birthDate ?? new Date(''),
        gender: state.dependentStore.spouse?.gender ?? undefined,
        ssn: event,
      }
    },
  }))

  store.on('addDependent', (state) => ({
    ...state,
    dependentStore: {
      ...state.dependentStore,
      dependents: addDependentToStore(state.dependentStore.dependents),
    },
  }))
  store.on('removeDependent', (state, element) => ({
    ...state,
    dependentStore: {
      ...state.dependentStore,
      dependents: removeDependentFromStore(state.dependentStore.dependents, element),
    },
  }))
  store.on('clearDependents', (state) => ({
    ...state,
    dependentStore: {
      ...state.dependentStore,
      dependents: [],
    },
  }))
  store.on('updateDependentFirstName', (state, {elIndex, element}) => ({
    ...state,
    dependentStore: {
      ...state.dependentStore,
      dependents: state.dependentStore.dependents.map((item, i) => {
        if(elIndex === i) {
          return {...item, firstName: element};
        }
        return item;
      })
    },
  }))
  store.on('updateDependentMiddleName', (state, {elIndex, element}) => ({
    ...state,
    dependentStore: {
      ...state.dependentStore,
      dependents: state.dependentStore.dependents.map((item, i) => {
        if(elIndex === i) {
          return {...item, middleName: element};
        }
        return item;
      })
    },
  }))
  store.on('updateDependentLastName', (state, {elIndex, element}) => ({
    ...state,
    dependentStore: {
      ...state.dependentStore,
      dependents: state.dependentStore.dependents.map((item, i) => {
        if(elIndex === i) {
          return {...item, lastName: element};
        }
        return item;
      })
    },
  }))
  store.on('updateDependentSuffix', (state, {elIndex, element}) => ({
    ...state,
    dependentStore: {
      ...state.dependentStore,
      dependents: state.dependentStore.dependents.map((item, i) => {
        if(elIndex === i) {
          return {...item, suffix: element};
        }
        return item;
      })
    },
  }))
  store.on('updateDependentGender', (state, {elIndex, element}) => ({
    ...state,
    dependentStore: {
      ...state.dependentStore,
      dependents: state.dependentStore.dependents.map((item, i) => {
        if(elIndex === i) {
          return {...item, gender: element};
        }
        return item;
      })
    },
  }))
  store.on('updateDependentBirthDate', (state, {elIndex, element}) => ({
    ...state,
    dependentStore: {
      ...state.dependentStore,
      dependents: state.dependentStore.dependents.map((item, i) => {
        if(elIndex === i) {
          return {...item, birthDate: element};
        }
        return item;
      })
    },
  }))
  store.on('updateDependentSSN', (state, {elIndex, element}) => ({
    ...state,
    dependentStore: {
      ...state.dependentStore,
      dependents: state.dependentStore.dependents.map((item, i) => {
        if(elIndex === i) {
          return {...item, ssn: element};
        }
        return item;
      })
    },
  }))
};

const initializeDependentStore = (): IDependentStore => ({
  dependentStore: {
    spouse: null,
    dependents: [],
  },
})

const addDependentToStore = (store: Person[]): Person[] => {
  const newStore = [...store];
  const newDep = new Person();
  newStore.push(newDep);

  return newStore;
};

const removeDependentFromStore = (store: Person[], index: number): Person[] => {
  const newStore = [...store];
  newStore.splice(index, 1);

  return newStore;
};

export default dependentStore;